import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * LA TAILLE DES FENÊTRES D'ENVOI — redimensionnables et MÉMORISÉES (demande de Bertrand, 2026-10-10, choix A)
 * ==========================================================================================================
 * Toutes les fenêtres d'envoi (individuelle, groupée, « Voir le message envoyé » ; Stage, Certification, Tuteurs,
 * CFA, Annuaire) sont LA MÊME fenêtre : une seule taille, partagée.
 *  · une poignée dans le coin bas-droit : on la tire pour agrandir / réduire (la fenêtre reste centrée : le coin suit
 *    la souris) ; double-clic = taille d'origine ;
 *  · bornes : au moins 640 × 420, au plus 96 % × 92 % de l'écran — la fenêtre ne sort jamais de l'écran, même si la
 *    taille a été choisie sur un écran plus grand ;
 *  · ⚠️ mémorisée PAR COMPTE ET PAR POSTE (localStorage, clé avec l'identifiant du compte) : une taille choisie sur un
 *    grand écran de bureau ne s'impose pas sur un portable. Stockage indisponible (navigation privée) → la fenêtre
 *    reste redimensionnable, sans mémoire.
 */
const MIN_L = 640, MIN_H = 420;
const cleStockage = () => {
    let id = 'anonyme';
    try { id = JSON.parse(localStorage.getItem('user') || '{}')?.id ?? 'anonyme'; } catch (_) { /* compte illisible */ }
    return `dm.fenetreEnvoi.taille.${id}`;
};
const lire = () => {
    try {
        const t = JSON.parse(localStorage.getItem(cleStockage()) || 'null');
        return t && Number.isFinite(t.l) && Number.isFinite(t.h) ? t : null;
    } catch (_) { return null; }
};
const ecrire = (t) => { try { if (t) localStorage.setItem(cleStockage(), JSON.stringify(t)); else localStorage.removeItem(cleStockage()); } catch (_) { /* sans mémoire */ } };
/** Bornée à l'écran du moment. */
const borner = (t) => {
    const maxL = Math.round(window.innerWidth * 0.96), maxH = Math.round(window.innerHeight * 0.92);
    return { l: Math.max(Math.min(MIN_L, maxL), Math.min(Math.round(t.l), maxL)), h: Math.max(Math.min(MIN_H, maxH), Math.min(Math.round(t.h), maxH)) };
};

/**
 * `const { classe, style, poignee } = useTailleFenetre();`
 * → `<div className={'stg-modale fe-fenetre' + classe} style={style}> … {poignee} </div>`
 */
export function useTailleFenetre() {
    const [taille, setTaille] = useState(() => lire());
    const glisse = useRef(null);
    const [, setEcran] = useState(0);
    // L'écran change (fenêtre du navigateur réduite) : les bornes suivent.
    useEffect(() => {
        const f = () => setEcran((n) => n + 1);
        window.addEventListener('resize', f);
        return () => window.removeEventListener('resize', f);
    }, []);

    const debut = useCallback((e) => {
        if (e.button !== 0) return;
        e.preventDefault(); e.stopPropagation();
        const r = e.currentTarget.parentElement.getBoundingClientRect();
        glisse.current = { x: e.clientX, y: e.clientY, l: r.width, h: r.height, t: null };
        const bouger = (ev) => {
            const g = glisse.current; if (!g) return;
            // Fenêtre centrée : elle grandit des deux côtés, le coin reste sous la souris.
            g.t = borner({ l: g.l + 2 * (ev.clientX - g.x), h: g.h + 2 * (ev.clientY - g.y) });
            setTaille(g.t);
        };
        const fin = () => {
            window.removeEventListener('pointermove', bouger);
            window.removeEventListener('pointerup', fin);
            if (glisse.current?.t) ecrire(glisse.current.t);
            glisse.current = null;
        };
        window.addEventListener('pointermove', bouger);
        window.addEventListener('pointerup', fin);
    }, []);
    const origine = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setTaille(null); ecrire(null); }, []);

    const t = taille ? borner(taille) : null;
    return {
        classe: t ? ' fe-fenetre-dimensionnee' : '',
        style: t ? { width: t.l, height: t.h, maxWidth: 'none', maxHeight: 'none' } : undefined,
        poignee: (
            <div className="fe-poignee" onPointerDown={debut} onDoubleClick={origine} onClick={(e) => e.stopPropagation()}
                 title="Tirer pour agrandir ou réduire la fenêtre (taille gardée pour vos prochains envois) · double-clic : taille d'origine"
                 role="separator" aria-label="Redimensionner la fenêtre" />
        )
    };
}
